/**
 * AI Model Selector Component
 * 
 * Dropdown for selecting the preferred AI model.
 * Used in Settings page.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Bot, ChevronDown, Check, Sparkles } from 'lucide-react';
import { useAI } from '../hooks/useAI';

export function AIModelSelector() {
  const { 
    models, 
    modelsLoading, 
    settings, 
    settingsLoading,
    updateModel,
    isUpdatingModel,
    isConfigured,
  } = useAI();

  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedModel = models.find(m => m.id === settings?.preferredModel) || models[0];
  const isLoading = modelsLoading || settingsLoading;

  if (!isConfigured) {
    return (
      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-3">
          <Bot className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              AI Analyst Not Configured
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Contact support to enable AI-powered trading insights.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-indigo-500" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          AI Model
        </h3>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Choose the AI model for generating trading insights and answering questions.
      </p>

      {/* Dropdown */}
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading || isUpdatingModel}
          className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-dark-card border border-gray-200 dark:border-gray-700 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors disabled:opacity-50"
        >
          <div className="flex items-center gap-3">
            <Bot className="w-5 h-5 text-indigo-500" />
            {isLoading ? (
              <span className="text-gray-500 dark:text-gray-400">Loading...</span>
            ) : (
              <div className="text-left">
                <span className="block text-gray-900 dark:text-white font-medium">
                  {selectedModel?.name || 'Select a model'}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  {selectedModel?.description}
                </span>
              </div>
            )}
          </div>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </motion.div>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-white dark:bg-dark-card border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden"
          >
            {models.map((model) => (
              <button
                key={model.id}
                onClick={() => {
                  updateModel(model.id);
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="text-left">
                  <span className="block text-gray-900 dark:text-white font-medium">
                    {model.name}
                    {model.isDefault && (
                      <span className="ml-2 text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full">
                        Default
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {model.description}
                  </span>
                </div>
                {settings?.preferredModel === model.id && (
                  <Check className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </div>

      {isUpdatingModel && (
        <p className="text-sm text-indigo-600 dark:text-indigo-400">
          Updating model preference...
        </p>
      )}
    </div>
  );
}

